// src/types/permission.types.ts

/**
 * Permission string format: "module.menu.action"
 * Example: "inventory.warehouse.adjust"
 */
export type PermissionString = string;

/**
 * User with permissions interface
 */
export interface UserWithPermissions {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
  permissions: PermissionString[];
}

/**
 * Parsed permission object
 */
export interface ParsedPermission {
  module: string;
  menu: string;
  action: string;
}

/**
 * Parse a permission string into its components
 * @param permissionString - Permission string like "inventory.warehouse.adjust"
 * @returns Parsed permission object
 */
export function parsePermission(permissionString: PermissionString): ParsedPermission {
  const parts = permissionString.split('.');
  if (parts.length !== 3) {
    throw new Error(
      `Invalid permission format: ${permissionString}. Expected format: module.menu.action`,
    );
  }
  return {
    module: parts[0],
    menu: parts[1],
    action: parts[2],
  };
}

/**
 * Check if a permission string is valid
 * @param permissionString - Permission string to validate
 * @returns true if valid
 */
export function isValidPermissionString(
  permissionString: string,
): permissionString is PermissionString {
  const parts = permissionString.split('.');
  return parts.length === 3 && parts.every((part) => part.length > 0);
}
